import { apiClient } from './client';

export type PlaudRecordingStatus =
  | 'PENDING'
  | 'DOWNLOADING'
  | 'DOWNLOADED'
  | 'MATCHED'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'
  | 'SKIPPED'
  | 'UNMATCHED';

export interface PlaudIntegrationStatus {
  connected: boolean;
  plaud_email: string | null;
  plaud_user_id: string | null;
  last_sync_at: string | null;
  unmatched_count: number;
  matched_count: number;
  failed_count: number;
}

export interface PlaudRecording {
  id: string;
  plaud_recording_id: string;
  title: string | null;
  recording_time: string;
  duration_seconds: number | null;
  status: PlaudRecordingStatus;
  match_failure_reason: string | null;
  matched_meeting_id: string | null;
  last_error: string | null;
}

export interface PlaudRecordingListResponse {
  items: PlaudRecording[];
  total: number;
  page: number;
  page_size: number;
}

export const plaudApi = {
  async getStatus(): Promise<PlaudIntegrationStatus> {
    const { data } = await apiClient.get<PlaudIntegrationStatus>(
      '/integrations/plaud',
    );
    return data;
  },

  async submitTokens(refreshToken: string): Promise<void> {
    await apiClient.post('/integrations/plaud/tokens', {
      refresh_token: refreshToken,
    });
  },

  async disconnect(): Promise<void> {
    await apiClient.delete('/integrations/plaud');
  },

  async syncNow(): Promise<{ task_id: string }> {
    const { data } = await apiClient.post<{ task_id: string }>(
      '/integrations/plaud/sync-now',
    );
    return data;
  },

  /** Transcribe every unmatched recording, each in the meeting it belongs to. */
  async adoptUnmatched(): Promise<{ task_id: string }> {
    const { data } = await apiClient.post<{ task_id: string }>(
      '/integrations/plaud/recordings/adopt-unmatched',
    );
    return data;
  },

  async listRecordings(
    opts: { status?: PlaudRecordingStatus; page?: number; pageSize?: number } = {},
  ): Promise<PlaudRecordingListResponse> {
    const { data } = await apiClient.get<PlaudRecordingListResponse>(
      '/integrations/plaud/recordings',
      {
        params: {
          status: opts.status,
          page: opts.page ?? 1,
          page_size: opts.pageSize ?? 20,
        },
      },
    );
    return data;
  },

  async matchToMeeting(
    recordingId: string,
    meetingId: string,
  ): Promise<PlaudRecording> {
    const { data } = await apiClient.post<PlaudRecording>(
      `/integrations/plaud/recordings/${recordingId}/match`,
      { meeting_id: meetingId },
    );
    return data;
  },

  async retry(recordingId: string): Promise<{ task_id: string }> {
    const { data } = await apiClient.post<{ task_id: string }>(
      `/integrations/plaud/recordings/${recordingId}/retry`,
    );
    return data;
  },

  async getBridgePresent(): Promise<boolean> {
    try {
      const { data } = await apiClient.get<{ present: boolean }>(
        '/bridge/presence',
      );
      return data.present;
    } catch {
      return false;
    }
  },
};
